import React from "react";
import { Link } from "react-router-dom";
import { BarChart3, Bell, Droplets, Shield, Wifi, Zap } from "lucide-react";
import { PageShell, PageHero, PageSection } from "../components/PageShell";
import { Button } from "../components/ui/button";

export default function Benefits() {
  const big = [
    { icon: Droplets, value: "3", label: "Readings", desc: "Soil moisture, air temperature, and humidity from one probe." },
    { icon: Zap, value: "Pump", label: "On the phone", desc: "Start, stop, a timer, or a rule that watches soil moisture." },
    { icon: BarChart3, value: "CSV", label: "History", desc: "Charts of past readings, and a download from the history page." },
  ];

  const grid = [
    { icon: Bell, t: "Alerts", d: "Set a limit. The app stores an alert when a reading crosses it." },
    { icon: Shield, t: "Sign-in", d: "The dashboard requires an account. A device is stored on the account that added it." },
    { icon: Wifi, t: "2.4 GHz Wi-Fi", d: "The ESP8266 board joins 2.4 GHz networks. You send the name from Settings." },
    { icon: Droplets, t: "Pump runs", d: "A run is stored with the soil, temperature, and humidity from when it started." },
    { icon: BarChart3, t: "One plot first", d: "The setup to prove is one growing area, one board, and one pump." },
    { icon: Bell, t: "While the board is on", d: "If the board has not sent a reading, the card shows N/A instead of an old number dressed up as live." },
  ];

  return (
    <PageShell>
      <PageHero
        eyebrow="What the app does"
        title="Readings and a pump switch."
        highlight="On your phone."
        description="We have not published a water-saved or yield number from a field trial. The items below are features you can open in the app."
      />

      <PageSection variant="content">
        <div className="mx-auto max-w-7xl grid md:grid-cols-3 gap-5">
          {big.map((b) => (
            <div
              key={b.label}
              className="rounded-3xl bg-card border border-border p-8"
            >
              <b.icon className="h-10 w-10 text-accent" />
              <div className="font-display text-5xl sm:text-6xl mt-6">{b.value}</div>
              <div className="font-display text-xl mt-1">{b.label}</div>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </PageSection>

      <PageSection tone="secondary">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12">
            <div className="text-[10px] uppercase tracking-[0.22em] text-accent mb-3">Also in the app</div>
            <h2 className="font-display text-4xl sm:text-5xl">Details that matter when you set it up</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {grid.map((g) => (
              <div key={g.t} className="rounded-2xl bg-card border border-border p-6">
                <g.icon className="h-7 w-7 text-accent" />
                <h3 className="font-display text-2xl mt-4">{g.t}</h3>
                <p className="text-sm text-muted-foreground mt-2">{g.d}</p>
              </div>
            ))}
          </div>
        </div>
      </PageSection>

      <PageSection className="text-center">
        <div className="mx-auto max-w-2xl">
          <h2 className="font-display text-4xl sm:text-5xl">Start with one board.</h2>
          <p className="mt-4 text-muted-foreground">Create an account, add a device id that has already been registered, and the readings show when the board is online.</p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link to="/signup"><Button variant="accent" size="lg">Create an account</Button></Link>
            <Link to="/contact"><Button variant="outline" size="lg">Ask a question</Button></Link>
          </div>
        </div>
      </PageSection>
    </PageShell>
  );
}
